import CampaignModel, { ParsedTreeBranchNode } from 'cio/models/campaign';
import ActionModel, { ActionType } from 'cio/models/action';
import { EdgePathSegment } from './layout/paths';

type BaseWorkflowGraphNode = {
  id: string;

  type: string;
  typeCategory: 'action' | 'virtual'; // distinguishes between nodes related to real workflow actions vs "fake" nodes we inserted

  // Size of the node
  width: number;
  height: number;

  // Coordinates of the center of the node
  x: number;
  y: number;

  // Stuff I haven't defined properly yet
  // TODO: Clean this up
  meta?: any;
  branch?: ParsedTreeBranchNode;
  branchId?: string;

  translateX?: number;
  translateY?: number;
};

// Node for a real workflow action
export type ActionWorkflowGraphNode = BaseWorkflowGraphNode & {
  type: ActionType;
  typeCategory: 'action';
  action: ActionModel;
};
// Event triggered campaigns don't have a "filter_match_delay_action" action, so
// we insert this fake one to have something to show in the workflow
export type EnterWorkflowGraphNode = BaseWorkflowGraphNode & {
  type: 'virtual_enter';
  typeCategory: 'virtual';
};
// Node for a branch action's path label
export type PathLabelWorkflowGraphNode = BaseWorkflowGraphNode & {
  type: 'virtual_path_label';
  typeCategory: 'virtual';
  // Refers to the index ordering of the path in the branch action, from left to
  // right
  index: number;
};
// Node for where a branch's paths rejoin
export type RejoinWorkflowGraphNode = BaseWorkflowGraphNode & {
  type: 'virtual_rejoin';
  typeCategory: 'virtual';
};
// Node for a drop area (the "+" areas when adding/moving actions)
export type DropAreaWorkflowGraphNode = BaseWorkflowGraphNode & {
  type: 'virtual_drop_area';
  typeCategory: 'virtual';
  source: WorkflowGraphNode;
  target: WorkflowGraphNode;
  edge: WorkflowGraphEdge;
};
// A dummy node is an invisible node we insert during the processing step
// between nodes that are connected across more than 1 layer. We replace the
// multi-layer edge with multiple one-layer edges and these dummy nodes.
export type DummyWorkflowGraphNode = BaseWorkflowGraphNode & {
  type: 'virtual_dummy';
  typeCategory: 'virtual';
  originalNode: WorkflowGraphNode;
  // Whether this is the last dummy node in a chain from the original source to
  // the target
  isLastIndex: boolean;
};
// During the processing step we replace all nodes with multiple nodes of unit
// height. This allows us to arrange workflow actions of different heights
// without unnecessarily stretching layers.
export type UnitWorkflowGraphNode = BaseWorkflowGraphNode & {
  type: 'virtual_unit';
  typeCategory: 'virtual';
  originalNode: WorkflowGraphNode;
  // The index of the unit node as it relates to the original node, counting
  // from the top
  index: number;
  // Whether this is the last unit node for a given node
  isLastIndex: boolean;
};

export type WorkflowGraphNode =
  | ActionWorkflowGraphNode
  | EnterWorkflowGraphNode
  | PathLabelWorkflowGraphNode
  | RejoinWorkflowGraphNode
  | DropAreaWorkflowGraphNode
  | DummyWorkflowGraphNode
  | UnitWorkflowGraphNode;

type BaseWorkflowGraphEdge = {
  // id of edge, we generate this when adding them, so that we can manage them
  // easier later on. The backend doesn't put ids on edges
  id: string;

  // id of source node
  from: string;

  // id of target node
  to: string;

  type: 'continue' | 'branch' | 'virtual' | 'unit';

  branchId?: string;
  originalEdge?: any;
};
export type ContinueWorkflowGraphEdge = BaseWorkflowGraphEdge & {
  type: 'continue';
};
export type BranchWorkflowGraphEdge = BaseWorkflowGraphEdge & {
  type: 'branch';
  index: number;
};
export type VirtualWorkflowGraphEdge = BaseWorkflowGraphEdge & {
  type: 'virtual';
};
export type UnitWorkflowGraphEdge = BaseWorkflowGraphEdge & {
  type: 'unit';
};
export type WorkflowGraphEdge =
  | ContinueWorkflowGraphEdge
  | BranchWorkflowGraphEdge
  | VirtualWorkflowGraphEdge
  | UnitWorkflowGraphEdge;

export type WorkflowGraphPath = {
  id: string;
  segment: EdgePathSegment;
  d: string;
  endInArrow?: boolean;
};

export class WorkflowGraph {
  campaign: CampaignModel;

  width = 0;
  height = 0;

  nodes: Record<string, WorkflowGraphNode> = {};
  edges: Record<string, WorkflowGraphEdge> = {};

  paths: WorkflowGraphPath[] = [];

  // The keys in this map are node ids, the arrays contain edges that leave a
  // given node
  outEdges: Record<string, WorkflowGraphEdge[]> = {};
  // The keys are node ids, the arrays are edges that point to a given node
  inEdges: Record<string, WorkflowGraphEdge[]> = {};

  // Used to assign IDs to virtual nodes we add during the layout algorithm
  virtualNodeIndex = 0;

  // Maps node IDs to ranks (layers)
  ranks: Record<string, number> = {};
  // Arrays of node IDs in each rank (layer)
  layeredRanks: Record<number, string[]> = {};
  // Keeps track of order of nodes while traversing it during the rank
  // assignments step
  traversalOrder: string[] = [];

  constructor(campaign: CampaignModel) {
    this.campaign = campaign;
  }

  addNode(node: WorkflowGraphNode) {
    if (this.nodes[node.id]) {
      throw new Error(`A graph node with id "${node.id}" already exists!`);
    }

    this.nodes[node.id] = node;
    this.outEdges[node.id] = [];
    this.inEdges[node.id] = [];
  }

  removeNode(id: string) {
    if (!this.nodes[id]) {
      throw new Error(`A graph node with id "${id}" doesn't exist!`);
    }

    delete this.nodes[id];

    let inEdges = this.inEdges[id];
    let outEdges = this.outEdges[id];
    let edgeIds = [...inEdges, ...outEdges].map((edge) => edge.id);

    delete this.inEdges[id];
    delete this.outEdges[id];
    edgeIds.forEach((id) => delete this.edges[id]);
  }

  addEdge(
    from: string,
    to: string,
    meta:
      | { type: 'continue'; branchId?: string; originalEdge?: any }
      | { type: 'virtual'; branchId?: string; originalEdge?: any }
      | { type: 'unit' }
      | { type: 'branch'; index: number; branchId?: string; originalEdge?: any },
  ) {
    let edge: WorkflowGraphEdge = {
      id: `${from}_${to}_${window.UUID.generate()}`,
      from,
      to,
      ...meta,
    };

    this.edges[edge.id] = edge;

    if (this.inEdges[edge.to] == null) {
      this.inEdges[edge.to] = [];
    }
    if (this.outEdges[edge.from] == null) {
      this.outEdges[edge.from] = [];
    }
    this.inEdges[edge.to].push(edge);
    this.outEdges[edge.from].push(edge);
  }

  removeEdge(id: string) {
    let edge = this.edges[id];

    this.inEdges[edge.to] = this.inEdges[edge.to].filter((edge) => edge.id !== id);
    this.outEdges[edge.from] = this.outEdges[edge.from].filter((edge) => edge.id !== id);
    delete this.edges[id];
  }
}
